import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { FlowsService } from "src/app/flows/flows.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import ResizeObserver from "resize-observer-polyfill";
import { takeUntil } from "rxjs/operators";
import { filter } from "lodash";

@Component({
  selector: "app-connector-option-menu",
  templateUrl: "./connector-option-menu.component.html",
  styleUrls: ["./connector-option-menu.component.scss"],
  host: { class: "flow-menu-component" },
})
export class ConnectorOptionMenuComponent implements OnInit {
  @Input() data!: any;
  @Input() mode: any;
  @Input() selected!: boolean;
  @Input() onMenuSizeChange!: Subject<void>;
  @ViewChild("connectorMenuElement") connectorMenuElement!: ElementRef;
  internal: boolean = false;

  /** Structured (legacy) conditions vs natural-language AI evaluation on the edge. */
  conditionMode: "structured" | "ai" = "structured";
  aiPromptControl = new FormControl("", []);
  /**
   * Per-edge toggle for the Responses API `web_search` tool. Default
   * true to preserve behavior for edges that pre-date this setting; the
   * global `FLOW_AI_CONDITIONS_WEB_SEARCH=false` env kill-switch still
   * wins if set.
   */
  aiWebSearchControl = new FormControl(true);

  conditions: any[] = [];
  filters!: any[];
  avlFilters!: any[];

  operators: any[] = [];
  isNullOptions!: { name: string; code: boolean }[];
  locationPermissionOptions!: (
    | { value: null; label: string }
    | { value: number; label: string }
  )[];
  emailStatusOptions!: (
    | { value: null; label: string }
    | { value: string; label: string }
  )[];
  booleanOptions!: { name: string; code: boolean }[];
  dayOfWeekOptions!: string[];
  conditionsData: any;
  timeOfDayConditionalChangeSubscription?: Subscription | null;
  hourOptions!: string[];
  minuteOptions!: string[];
  pmOptions!: { name: string; code: boolean }[];
  onMenuOpenChange = new BehaviorSubject<boolean>(!!this.selected);
  onConnectorMenuInstantiated = new Subject<void>();
  mutationObserver!: MutationObserver;
  resizeObserver!: ResizeObserver;
  private onComponentDestroy = new Subject();
  

  constructor(
    private SessionService: SessionService,
    private FlowsService: FlowsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {

    //console.log('CONNECTOR OPTION MENU NG INIT', this.data)

    this.conditionMode =
      this.data.condition_mode === "ai" ? "ai" : "structured";
    this.aiPromptControl.setValue(this.data.ai_prompt || "");
    // Default to true when the field is missing (existing edges that
    // predate the checkbox) so behavior is unchanged by this rollout.
    this.aiWebSearchControl.setValue(
      this.data.ai_web_search_enabled !== false,
    );

    this.conditionsData = this.data.conditions || [];
    this.booleanOptions = this.FlowsService.booleanOptions;
    this.locationPermissionOptions = this.FlowsService.locationPermissionOptions;
    this.dayOfWeekOptions = this.FlowsService.dayOfWeekOptions;
    this.emailStatusOptions = this.FlowsService.emailStatusOptions;

    this.filters = this.FlowsService.getConditions(this.internal, this.data.trigger, this.data.sourceType, this.data.targetType, this.data.is_follow_up);

    //console.log('CONNECTOR MENU FILTERS', this.filters)

    let count = 0;

    //console.log('CONDITIONS DATA', this.conditionsData)

    //console.log('FILTERS', this.filters)

    // build conditions
    for (let conditionData of this.conditionsData) {

      //console.log('------')

      //console.log('CONDITION DATA', conditionData)

      const conditional = this.filters.find((filter) => {
        return (conditionData.group === 'User' && filter.id === conditionData.attribute_data.id) || (conditionData.group !== 'User' && filter.name === conditionData.name);
      });

      //console.log('CONDITIONAL', conditional)

      const condition = {
        uuid: Math.random().toString(16).slice(2, 8),
        formGroup: new FormGroup({
          conditional: new FormControl(conditional, [Validators.required]),
          operator: new FormControl(conditionData.operator, [
            Validators.required,
          ]),
        }),
      };

      const data = this.getConditionValueData(conditionData);

      this.initCondition(condition, data);

      this.conditions.push(condition);

      if (count < this.conditionsData.length - 1) {
        this.operators.push("and");
      }
      count++;

      
    }

    this.ensureFollowUpTimeAfterSentCondition();

    this.hourOptions = this.FlowsService.hourOptions;

    this.minuteOptions = this.FlowsService.minuteOptions;

    this.pmOptions = this.FlowsService.pmOptions;

    // remove time after sent from filters after it's been handled above
    //this.avlFilters = filter(this.filters, (f) => f.id != "timeAfterSent")
  }

  ngAfterViewInit() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        //If hidden changes, emit value to subject
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "hidden"
        ) {
          this.onMenuOpenChange.next(
            !this.connectorMenuElement.nativeElement.hidden,
          );
        }
      });
    });

    this.mutationObserver.observe(this.connectorMenuElement.nativeElement, {
      attributes: true,
      childList: true,
      characterData: true,
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      if (this.isOpen) {
        this.onMenuSizeChange.next();
      }
    });

    this.resizeObserver.observe(this.connectorMenuElement.nativeElement);

    this.onConnectorMenuInstantiated.next();
    this.onConnectorMenuInstantiated.complete();
  }

  ngOnDestroy() {
    this.mutationObserver.disconnect();
    this.resizeObserver.disconnect();
    if (this.isOpen) {
      this.onMenuOpenChange.next(false);
    }
    this.onMenuOpenChange.complete();

    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get conditionsValid() {
    if (this.conditionMode === "ai") {
      return !!(this.aiPromptControl.value || "").trim();
    }
    for (let condition of this.conditions) {
      if (condition.formGroup.invalid) {
        return false;
      }
    }

    return true;
  }

  /**
   * Follow-up flows require a locked "time after sent" row when in structured mode.
   */
  ensureFollowUpTimeAfterSentCondition(): void {
    if (!this.data.is_follow_up || this.conditionMode === "ai") {
      return;
    }

    let timeAfterSentCondition = this.conditions.find(
      (condition) =>
        condition.formGroup.controls.conditional.value?.id === "timeAfterSent",
    );

    if (!timeAfterSentCondition) {
      const newCondition = this.addCondition();
      newCondition.formGroup.controls.conditional.setValue(this.filters[0]);
      this.resetCondition(newCondition);
      newCondition.formGroup.controls.operator.setValue("===");
      timeAfterSentCondition = newCondition;
    }

    timeAfterSentCondition.formGroup.controls.conditional.disable();
    timeAfterSentCondition.formGroup.controls.operator.disable();
  }

  private clearStructuredConditions(): void {
    for (const c of this.conditions) {
      c.operatorChangeSubscription?.unsubscribe();
    }
    this.conditions = [];
    this.operators = [];
  }

  requestConditionMode(target: "structured" | "ai", $event?: Event): void {
    $event?.stopPropagation();
    if (target === this.conditionMode) {
      return;
    }

    const hasStructuredRows = this.conditions.length > 0;
    const hasAiPrompt = !!(this.aiPromptControl.value || "").trim();

    if (target === "ai" && this.conditionMode === "structured" && hasStructuredRows) {
      this.dialog
        .open(AreYouSureDialogComponent, {
          data: {
            areYouSureInfo: {
              title: "Switch to AI prompt?",
              description:
                "Your structured conditions for this connection will be removed. You can switch back later, but you will need to re-enter them.",
              continueBtn: {
                text: "Switch and clear",
                color: "primary",
              },
              cancelBtn: {
                text: "Cancel",
                color: "lightGrey",
              },
            },
          },
        })
        .afterClosed()
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.clearStructuredConditions();
            this.conditionMode = "ai";
          }
        });
      return;
    }

    if (
      target === "structured" &&
      this.conditionMode === "ai" &&
      hasAiPrompt
    ) {
      this.dialog
        .open(AreYouSureDialogComponent, {
          data: {
            areYouSureInfo: {
              title: "Switch to structured conditions?",
              description:
                "Your AI prompt for this connection will be cleared. You can switch back to AI prompt later and enter a new one.",
              continueBtn: {
                text: "Switch and clear",
                color: "primary",
              },
              cancelBtn: {
                text: "Cancel",
                color: "lightGrey",
              },
            },
          },
        })
        .afterClosed()
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.aiPromptControl.setValue("");
            this.conditionMode = "structured";
            this.ensureFollowUpTimeAfterSentCondition();
          }
        });
      return;
    }

    this.conditionMode = target;
    if (target === "structured") {
      this.ensureFollowUpTimeAfterSentCondition();
    }
  }

  // addFollowUpCondition(){
  //   const condition = {
  //     uuid: Math.random().toString(16).slice(2, 8),
  //     formGroup: new FormGroup({
  //       conditional: new FormControl(, [Validators.required]),
  //       operator: new FormControl(null, [Validators.required]),
  //     }),
  //   };

  //   this.conditions.push(condition);

  //   this.operators.push("and");
  // }

  addCondition($event?: any, atBeginning = false) {
    $event?.stopPropagation();

    const condition = {
      uuid: Math.random().toString(16).slice(2, 8),
      formGroup: new FormGroup({
        conditional: new FormControl(null, [Validators.required]),
        operator: new FormControl(null, [Validators.required]),
      }),
    };

    if (!atBeginning) {
      this.conditions.push(condition);
      this.operators.push("and");
    } else {
      this.conditions.unshift(condition);
      this.operators.unshift("and");
    }
    //console.log(this.conditions);

    return condition;
  }

  changeOperator($event: any, index: number) {
    $event.stopPropagation();
    //TODO Uncomment when we figure out what to do with operators on flow conditions

    // if (this.operators[index] === "and") {
    //   this.operators[index] = "or";
    // } else {
    //   this.operators[index] = "and";
    // }
  }

  resetCondition(condition: { uuid: string; formGroup: FormGroup }) {
    //console.log('RESET CONDITION')
    condition.formGroup.controls.operator.reset();
    condition.formGroup.removeControl("value");

    condition.formGroup.removeControl("tosDays");
    condition.formGroup.removeControl("tosHours");
    condition.formGroup.removeControl("tosMinutes");

    this.initCondition(condition);
  }

  initCondition(
    condition: {
      uuid: string;
      formGroup: FormGroup;
      operatorChangeSubscription?: Subscription | null;
    },
    data?: any,
  ) {

    //console.log('INIT CONDITION')

    //If condition already has a operatorChangeSubscription on it then reset it
    if (condition.operatorChangeSubscription) {
      condition.operatorChangeSubscription.unsubscribe();
      condition.operatorChangeSubscription = null;
    }

    if(condition.formGroup.controls.conditional.value.comparisons.length === 1){
      condition.formGroup.controls.operator.setValue(condition.formGroup.controls.conditional.value.comparisons[0].operator);
      condition.formGroup.controls.operator.disable();
    } else {
      condition.formGroup.controls.operator.enable();
    }

    if (
      condition.formGroup.controls.conditional.value?.type === "timeOnStation" ||
      condition.formGroup.controls.conditional.value?.type === "lastVisit" ||
      condition.formGroup.controls.conditional.value?.type === "timeAfterSent"
    ) {
      condition.formGroup.addControl(
        "tosDays",
        new FormControl(data?.value ? data.value.days ?? 0 : null, [
          Validators.required,
        ]),
      );
      condition.formGroup.addControl(
        "tosHours",
        new FormControl(data?.value ? data.value.hours ?? 0 : null, [
          Validators.required,
        ]),
      );
      condition.formGroup.addControl(
        "tosMinutes",
        new FormControl(data?.value ? data.value.minutes ?? 0 : null, [
          Validators.required,
        ]),
      );
    } else if (
      condition.formGroup.controls.conditional.value?.type === "timeOfDay"
    ) {
      //If operator already has value initialize time of day form fields
      if (condition.formGroup.controls.operator.value) {
        this.initTimeOfDayValueForm(
          condition,
          condition.formGroup.controls.operator.value,
          data,
        );
      }

      condition.operatorChangeSubscription =
        condition.formGroup.controls.operator.valueChanges
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe((operator) => {
            if (operator) {
              this.initTimeOfDayValueForm(condition, operator);
            }
          });
    } else {
      let value;
      if (
        condition.formGroup.controls.conditional.value?.type ===
        "locationPermission"
      ) {
        value = data?.value?.locpermissions ?? [];
      } else if (
        condition.formGroup.controls.conditional.value?.type === "dayOfWeek"
      ) {
        value = data?.value?.weekdays ?? [];
      } else {
        value = data?.value !== undefined ? data?.value : null;
      }

      condition.formGroup.addControl(
        "value",
        new FormControl(value, [Validators.required]),
      );
    }
  }

  removeCondition(condition: any) {
    this.conditions = this.conditions.filter((cond) => {
      return cond.uuid !== condition.uuid;
    });
  }

  getConditionValueData(conditionData: any) {
    const data: any = { type: conditionData.type };

    if (conditionData.type === "boolean" || conditionData.type === "isNull" || conditionData.type == "signalRead") {
      data.value = conditionData.on === "true";
    } else {
      data.value = conditionData.on;
    }

    return data;
  }

  initTimeOfDayValueForm(condition: any, operator: string, data?: any) {
    if (operator === "before" || operator === "after") {
      let timeOfDayHour, timeOfDayMinutes, timeOfDayPM;
      if (data?.value?.timeOfDayDate) {
        const timeOfDayObj = this.getTimeOfDayObj(data.value.timeOfDayDate);
        timeOfDayHour = timeOfDayObj.timeOfDayHour;
        timeOfDayMinutes = timeOfDayObj.timeOfDayMinutes;
        timeOfDayPM = timeOfDayObj.timeOfDayPM;
      }

      condition.formGroup.addControl(
        "timeOfDayHour",
        new FormControl(timeOfDayHour, [Validators.required]),
      );
      condition.formGroup.addControl(
        "timeOfDayMinutes",
        new FormControl(timeOfDayMinutes, [Validators.required]),
      );
      condition.formGroup.addControl(
        "timeOfDayPM",
        new FormControl(timeOfDayPM, [Validators.required]),
      );
    } else if (operator === "between") {
      let startTimeOfDayHour,
        startTimeOfDayMinutes,
        startTimeOfDayPM,
        endTimeOfDayHour,
        endTimeOfDayMinutes,
        endTimeOfDayPM;

      if (data?.value?.startTimeOfDayDate) {
        const timeOfDayObj = this.getTimeOfDayObj(
          data.value.startTimeOfDayDate,
        );
        startTimeOfDayHour = timeOfDayObj.timeOfDayHour;
        startTimeOfDayMinutes = timeOfDayObj.timeOfDayMinutes;
        startTimeOfDayPM = timeOfDayObj.timeOfDayPM;
      }

      if (data?.value?.endTimeOfDayDate) {
        const timeOfDayObj = this.getTimeOfDayObj(data.value.endTimeOfDayDate);
        endTimeOfDayHour = timeOfDayObj.timeOfDayHour;
        endTimeOfDayMinutes = timeOfDayObj.timeOfDayMinutes;
        endTimeOfDayPM = timeOfDayObj.timeOfDayPM;
      }

      condition.formGroup.addControl(
        "startTimeOfDayHour",
        new FormControl(startTimeOfDayHour, [Validators.required]),
      );
      condition.formGroup.addControl(
        "startTimeOfDayMinutes",
        new FormControl(startTimeOfDayMinutes, [Validators.required]),
      );
      condition.formGroup.addControl(
        "startTimeOfDayPM",
        new FormControl(startTimeOfDayPM, [Validators.required]),
      );

      condition.formGroup.addControl(
        "endTimeOfDayHour",
        new FormControl(endTimeOfDayHour, [Validators.required]),
      );
      condition.formGroup.addControl(
        "endTimeOfDayMinutes",
        new FormControl(endTimeOfDayMinutes, [Validators.required]),
      );
      condition.formGroup.addControl(
        "endTimeOfDayPM",
        new FormControl(endTimeOfDayPM, [Validators.required]),
      );
    }
  }

  getTimeOfDayObj(timeOfDay: string) {
    const timeOfDayDate = new Date(timeOfDay);

    let timeOfDayHour: any, timeOfDayMinutes: any, timeOfDayPM: boolean;

    timeOfDayHour = timeOfDayDate.getHours();
    timeOfDayMinutes = timeOfDayDate.getMinutes();

    if (timeOfDayHour > 12) {
      timeOfDayHour = timeOfDayHour - 12;
      timeOfDayPM = true;
    } else if (timeOfDayHour === 12) {
      timeOfDayPM = true;
    } else if (timeOfDayHour === 0) {
      timeOfDayHour = 12;
      timeOfDayPM = false;
    } else {
      timeOfDayPM = false;
    }

    timeOfDayHour = timeOfDayHour.toString();
    timeOfDayMinutes = timeOfDayMinutes.toString();

    if (timeOfDayHour.length < 2) {
      timeOfDayHour = `0${timeOfDayHour}`;
    }
    if (timeOfDayMinutes.length < 2) {
      timeOfDayMinutes = `0${timeOfDayMinutes}`;
    }

    return {
      timeOfDayHour,
      timeOfDayMinutes,
      timeOfDayPM,
    };
  }

  get isOpen() {
    return this.selected;
  }

  get conditionsDTO() {
    const conditions: any = [];
    let count = 0;
    for (let condition of this.conditions) {
      let onValue;

      if (
        condition.formGroup.controls.conditional.value?.type ===
          "timeOnStation" ||
        condition.formGroup.controls.conditional.value?.type === "lastVisit" ||
        condition.formGroup.controls.conditional.value?.type === "timeAfterSent"
      ) {
        onValue = {
          days: Number.parseInt(condition.formGroup.controls.tosDays.value),
          hours: Number.parseInt(condition.formGroup.controls.tosHours.value),
          minutes: Number.parseInt(
            condition.formGroup.controls.tosMinutes.value,
          ),
        };
      } else if (
        condition.formGroup.controls.conditional.value?.type === "timeOfDay"
      ) {
        if (
          condition.formGroup.controls.operator.value === "before" ||
          condition.formGroup.controls.operator.value === "after"
        ) {
          const timeOfDayDate = new Date();

          let hour = condition.formGroup.controls.timeOfDayHour.value;

          if (hour === "12") {
            hour = "00";
          }

          timeOfDayDate.setHours(
            Number.parseInt(condition.formGroup.controls.timeOfDayHour.value),
          );
          if (condition.formGroup.controls.timeOfDayPM.value) {
            timeOfDayDate.setHours(timeOfDayDate.getHours() + 12);
          }
          timeOfDayDate.setMinutes(
            Number.parseInt(
              condition.formGroup.controls.timeOfDayMinutes.value,
            ),
          );

          onValue = {
            timeOfDayDate,
          };
        } else if (condition.formGroup.controls.operator.value === "between") {
          const startTimeOfDayDate = new Date();
          const endTimeOfDayDate = new Date();

          let startHour = condition.formGroup.controls.startTimeOfDayHour.value;
          let endHour = condition.formGroup.controls.endTimeOfDayHour.value;

          if (startHour === "12") {
            startHour = "00";
          }
          if (endHour === "12") {
            endHour = "00";
          }

          startTimeOfDayDate.setHours(Number.parseInt(startHour));
          if (condition.formGroup.controls.startTimeOfDayPM.value) {
            startTimeOfDayDate.setHours(startTimeOfDayDate.getHours() + 12);
          }
          startTimeOfDayDate.setMinutes(
            Number.parseInt(
              condition.formGroup.controls.startTimeOfDayMinutes.value,
            ),
          );

          endTimeOfDayDate.setHours(Number.parseInt(endHour));
          if (condition.formGroup.controls.endTimeOfDayPM.value) {
            endTimeOfDayDate.setHours(endTimeOfDayDate.getHours() + 12);
          }
          endTimeOfDayDate.setMinutes(
            Number.parseInt(
              condition.formGroup.controls.endTimeOfDayMinutes.value,
            ),
          );

          onValue = {
            startTimeOfDayDate,
            endTimeOfDayDate,
          };
        }
      } else if (
        condition.formGroup.controls.conditional.value?.type === "integer"
      ) {
        onValue = Number.parseInt(condition.formGroup.controls.value.value);
      } else if (
        condition.formGroup.controls.conditional.value?.type === "float"
      ) {
        onValue = Number.parseFloat(condition.formGroup.controls.value.value);
      } else if (
        condition.formGroup.controls.conditional.value?.type ===
        "locationPermission"
      ) {
        onValue = {
          locpermissions: condition.formGroup.controls.value.value,
        };
      } else if (
        condition.formGroup.controls.conditional.value?.type === "dayOfWeek"
      ) {
        onValue = { weekdays: condition.formGroup.controls.value.value };
      } else {
        onValue = condition.formGroup.controls.value.value;
        if (onValue instanceof Date) {
          onValue = onValue.toISOString();
        } else if (onValue === true || onValue === false) {
          onValue = onValue.toString();
        }
      }

      conditions.push({
        attribute_data: condition.formGroup.controls.conditional.value,
        id: count,
        group: condition.formGroup.controls.conditional.value.group,
        label: condition.formGroup.controls.conditional.value.label,
        name: condition.formGroup.controls.conditional.value.name,
        type: condition.formGroup.controls.conditional.value.type,
        value: condition.formGroup.controls.conditional.value.value,
        operator: condition.formGroup.controls.operator.value,
        operators: condition.formGroup.controls.conditional.value.comparisons,
        operator_data:
          condition.formGroup.controls.conditional.value.comparisons.find(
            (operator: any) =>
              operator.operator === condition.formGroup.controls.operator.value,
          ),

        on: onValue,
      });
      count++;
    }

    return conditions;
  }

  /** Persisted under `logic.edges[].data` — includes conditions and AI fields. */
  get edgeDataDTO() {
    const isFollowUp = !!this.data.is_follow_up;
    const base: Record<string, unknown> = {
      is_follow_up: isFollowUp,
      condition_mode: this.conditionMode,
    };
    if (this.conditionMode === "ai") {
      base.conditions = [];
      base.ai_prompt = (this.aiPromptControl.value || "").trim();
      base.ai_web_search_enabled = !!this.aiWebSearchControl.value;
      return base;
    }
    base.conditions = this.conditionsDTO;
    return base;
  }
}
